import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AdminDoctorDetailResponse,
  AdminDoctorLicence,
  DoctorAdminActionAuditEntry,
  DoctorAdminSearchParams,
  DoctorLicenceGrantHistoryEntry,
  DoctorPublicResponse,
  GrantFreeAccessPayload,
  SmsStatusResponse,
} from "../../types/doctorAdmin.definition";
import {
  PaginationSettingsResponse,
  SearchResponse,
} from "../../types/search.definition";

interface DoctorAdminState {
  results: DoctorPublicResponse[];
  pagination: PaginationSettingsResponse | null;
  resultsLoading: boolean;
  resultsError: string | null;
  selectedDoctor: AdminDoctorDetailResponse | null;
  detailLoading: boolean;
  detailError: string | null;
  grantSaving: boolean;
  grantError: string | null;
  grantHistory: DoctorLicenceGrantHistoryEntry[];
  grantHistoryLoading: boolean;
  grantHistoryError: string | null;
  smsStatus: SmsStatusResponse | null;
  smsStatusLoading: boolean;
  smsStatusError: string | null;
  auditLog: DoctorAdminActionAuditEntry[];
  auditLogLoading: boolean;
  auditLogError: string | null;
}

const initialState: DoctorAdminState = {
  results: [],
  pagination: null,
  resultsLoading: false,
  resultsError: null,
  selectedDoctor: null,
  detailLoading: false,
  detailError: null,
  grantSaving: false,
  grantError: null,
  grantHistory: [],
  grantHistoryLoading: false,
  grantHistoryError: null,
  smsStatus: null,
  smsStatusLoading: false,
  smsStatusError: null,
  auditLog: [],
  auditLogLoading: false,
  auditLogError: null,
};

// ── Actions ───────────────────────────────────────────────────────────────

export const fetchDoctorsRequest = createAction<DoctorAdminSearchParams>(
  "doctorAdmin/fetchDoctorsRequest"
);

export const fetchDoctorsSuccess = createAction<
  SearchResponse<DoctorPublicResponse>
>("doctorAdmin/fetchDoctorsSuccess");

export const fetchDoctorsFailure = createAction<string>(
  "doctorAdmin/fetchDoctorsFailure"
);

export const fetchDoctorDetailRequest = createAction<number | string>(
  "doctorAdmin/fetchDoctorDetailRequest"
);

export const fetchDoctorDetailSuccess = createAction<AdminDoctorDetailResponse>(
  "doctorAdmin/fetchDoctorDetailSuccess"
);

export const fetchDoctorDetailFailure = createAction<string>(
  "doctorAdmin/fetchDoctorDetailFailure"
);

// MED-1253: grant (or extend) free access.

export const grantFreeAccessRequest = createAction<{
  doctorId: number | string;
  data: GrantFreeAccessPayload;
  onSuccess?: (licence: AdminDoctorLicence) => void;
  onError?: (code: string) => void;
}>("doctorAdmin/grantFreeAccessRequest");

export const grantFreeAccessSuccess = createAction<{
  doctorId: number | string;
  licence: AdminDoctorLicence;
}>("doctorAdmin/grantFreeAccessSuccess");

export const grantFreeAccessFailure = createAction<string>(
  "doctorAdmin/grantFreeAccessFailure"
);

// MED-1253: grant/extension history.

export const fetchGrantHistoryRequest = createAction<number | string>(
  "doctorAdmin/fetchGrantHistoryRequest"
);

export const fetchGrantHistorySuccess = createAction<
  DoctorLicenceGrantHistoryEntry[]
>("doctorAdmin/fetchGrantHistorySuccess");

export const fetchGrantHistoryFailure = createAction<string>(
  "doctorAdmin/fetchGrantHistoryFailure"
);

// MED-1254: SMS notification diagnostics (enabled, consent, phone verified).

export const fetchSmsStatusRequest = createAction<number | string>(
  "doctorAdmin/fetchSmsStatusRequest"
);

export const fetchSmsStatusSuccess = createAction<SmsStatusResponse>(
  "doctorAdmin/fetchSmsStatusSuccess"
);

export const fetchSmsStatusFailure = createAction<string>(
  "doctorAdmin/fetchSmsStatusFailure"
);

// MED-1255: audit log of sensitive admin actions taken on a doctor's account.

export const fetchAuditLogRequest = createAction<number | string>(
  "doctorAdmin/fetchAuditLogRequest"
);

export const fetchAuditLogSuccess = createAction<DoctorAdminActionAuditEntry[]>(
  "doctorAdmin/fetchAuditLogSuccess"
);

export const fetchAuditLogFailure = createAction<string>(
  "doctorAdmin/fetchAuditLogFailure"
);

// ── Slice ─────────────────────────────────────────────────────────────────

const doctorAdminSlice = createSlice({
  name: "doctorAdmin",
  initialState,
  reducers: {
    clearError: state => {
      state.resultsError = null;
      state.detailError = null;
    },
    clearSelectedDoctor: state => {
      // Reset every doctor-scoped slice, not just the detail — otherwise a
      // re-mount (e.g. navigating from one doctor's page to another) can
      // briefly render the previous doctor's grant history/audit log while
      // the new fetches are in flight.
      state.selectedDoctor = null;
      state.grantHistory = [];
      state.grantHistoryError = null;
      state.auditLog = [];
      state.auditLogError = null;
    },
    clearSmsStatus: state => {
      state.smsStatus = null;
      state.smsStatusError = null;
    },
  },
  extraReducers: builder => {
    // Search / list
    builder
      .addCase(fetchDoctorsRequest, state => {
        state.resultsLoading = true;
        state.resultsError = null;
      })
      .addCase(
        fetchDoctorsSuccess,
        (
          state,
          action: PayloadAction<SearchResponse<DoctorPublicResponse>>
        ) => {
          state.resultsLoading = false;
          state.results = action.payload.data;
          state.pagination = action.payload.paginationSettings;
          state.resultsError = null;
        }
      )
      .addCase(fetchDoctorsFailure, (state, action: PayloadAction<string>) => {
        state.resultsLoading = false;
        state.resultsError = action.payload;
      });

    // Detail
    builder
      .addCase(fetchDoctorDetailRequest, state => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(
        fetchDoctorDetailSuccess,
        (state, action: PayloadAction<AdminDoctorDetailResponse>) => {
          state.detailLoading = false;
          state.selectedDoctor = action.payload;
          state.detailError = null;
        }
      )
      .addCase(
        fetchDoctorDetailFailure,
        (state, action: PayloadAction<string>) => {
          state.detailLoading = false;
          state.detailError = action.payload;
        }
      );

    // Grant / extend free access
    builder
      .addCase(grantFreeAccessRequest, state => {
        state.grantSaving = true;
        state.grantError = null;
      })
      .addCase(
        grantFreeAccessSuccess,
        (
          state,
          action: PayloadAction<{
            doctorId: number | string;
            licence: AdminDoctorLicence;
          }>
        ) => {
          state.grantSaving = false;
          state.grantError = null;
          // Guard against a stale response landing after the user has already
          // navigated to a different doctor's detail page.
          if (
            state.selectedDoctor &&
            String(state.selectedDoctor.id) === String(action.payload.doctorId)
          ) {
            state.selectedDoctor = {
              ...state.selectedDoctor,
              licence: action.payload.licence,
            };
          }
        }
      )
      .addCase(
        grantFreeAccessFailure,
        (state, action: PayloadAction<string>) => {
          state.grantSaving = false;
          state.grantError = action.payload;
        }
      );

    // Grant/extension history
    builder
      .addCase(fetchGrantHistoryRequest, state => {
        state.grantHistoryLoading = true;
        state.grantHistoryError = null;
      })
      .addCase(
        fetchGrantHistorySuccess,
        (state, action: PayloadAction<DoctorLicenceGrantHistoryEntry[]>) => {
          state.grantHistoryLoading = false;
          state.grantHistory = action.payload;
          state.grantHistoryError = null;
        }
      )
      .addCase(
        fetchGrantHistoryFailure,
        (state, action: PayloadAction<string>) => {
          state.grantHistoryLoading = false;
          state.grantHistoryError = action.payload;
        }
      );

    // SMS notification diagnostics
    builder
      .addCase(fetchSmsStatusRequest, state => {
        state.smsStatusLoading = true;
        state.smsStatusError = null;
      })
      .addCase(
        fetchSmsStatusSuccess,
        (state, action: PayloadAction<SmsStatusResponse>) => {
          state.smsStatusLoading = false;
          state.smsStatus = action.payload;
          state.smsStatusError = null;
        }
      )
      .addCase(
        fetchSmsStatusFailure,
        (state, action: PayloadAction<string>) => {
          state.smsStatusLoading = false;
          state.smsStatusError = action.payload;
        }
      );

    // Audit log of sensitive admin actions
    builder
      .addCase(fetchAuditLogRequest, state => {
        state.auditLogLoading = true;
        state.auditLogError = null;
      })
      .addCase(
        fetchAuditLogSuccess,
        (state, action: PayloadAction<DoctorAdminActionAuditEntry[]>) => {
          state.auditLogLoading = false;
          state.auditLog = action.payload;
          state.auditLogError = null;
        }
      )
      .addCase(fetchAuditLogFailure, (state, action: PayloadAction<string>) => {
        state.auditLogLoading = false;
        state.auditLogError = action.payload;
      });
  },
});

export const { clearError, clearSelectedDoctor, clearSmsStatus } =
  doctorAdminSlice.actions;

// ── Selectors ─────────────────────────────────────────────────────────────

export const selectDoctorResults = (state: { doctorAdmin: DoctorAdminState }) =>
  state.doctorAdmin.results;
export const selectDoctorPagination = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.pagination;
export const selectDoctorsResultsLoading = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.resultsLoading;
export const selectDoctorsResultsError = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.resultsError;
export const selectSelectedDoctor = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.selectedDoctor;
export const selectDoctorDetailLoading = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.detailLoading;
export const selectDoctorDetailError = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.detailError;
export const selectGrantSaving = (state: { doctorAdmin: DoctorAdminState }) =>
  state.doctorAdmin.grantSaving;
export const selectGrantError = (state: { doctorAdmin: DoctorAdminState }) =>
  state.doctorAdmin.grantError;
export const selectGrantHistory = (state: { doctorAdmin: DoctorAdminState }) =>
  state.doctorAdmin.grantHistory;
export const selectGrantHistoryLoading = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.grantHistoryLoading;
export const selectGrantHistoryError = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.grantHistoryError;
export const selectSmsStatus = (state: { doctorAdmin: DoctorAdminState }) =>
  state.doctorAdmin.smsStatus;
export const selectSmsStatusLoading = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.smsStatusLoading;
export const selectSmsStatusError = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.smsStatusError;
export const selectAuditLog = (state: { doctorAdmin: DoctorAdminState }) =>
  state.doctorAdmin.auditLog;
export const selectAuditLogLoading = (state: {
  doctorAdmin: DoctorAdminState;
}) => state.doctorAdmin.auditLogLoading;
export const selectAuditLogError = (state: { doctorAdmin: DoctorAdminState }) =>
  state.doctorAdmin.auditLogError;

export default doctorAdminSlice.reducer;
